import { MAX_ZNAKOW_WIADOMOSCI_TRENINGU } from "@shared/treningTypy.js";
import type { BladAkcji } from "../../hooks/useTrening.js";
import { IconOstrzezenie } from "../icons/index.js";
import { Button } from "../ui/Button.js";
import { Textarea } from "../ui/Field.js";
import { IkonaWyslij } from "./ikony.js";
import { LicznikZnakow } from "./LicznikZnakow.js";

const ID_POLA = "trening-odpowiedz";

/**
 * Pole odpowiedzi w rozmowie z rozmowca: etykieta, pole, licznik znakow, przycisk "Wyślij" i miejsce na blad.
 *
 * Blokada (oczekiwanie na rozmowce, limit odpowiedzi) to `readOnly` plus `aria-disabled`, NIE natywne `disabled`: pole z
 * fokusem, ktoremu ustawiono `disabled`, oddaje fokus <body> i uzytkownik klawiatury laduje na poczatku strony (A-4; to samo
 * robi Button przy `zajety`). Tekst zostaje w polu, takze po bledzie: nic nie ginie (AI-4).
 *
 * Ctrl+Enter (albo Cmd+Enter) wysyla, sam Enter dopisuje nowa linie: odpowiedzi bywaja kilkuakapitowe.
 */
export function PoleOdpowiedzi({
  wartosc,
  onZmiana,
  onWyslij,
  onOdswiez,
  zablokowane,
  wysylanie,
  podpowiedz,
  blad,
  poleRef,
}: {
  wartosc: string;
  onZmiana: (tekst: string) => void;
  onWyslij: () => void;
  /** Sprawdzenie rozmowy po odswiezeniu strony w trakcie odpowiedzi rozmowcy (tylko gdy `onOdswiez` podano). */
  onOdswiez?: () => void;
  /** Pole tylko do odczytu: czekamy na rozmowce albo osiagnieto limit odpowiedzi. */
  zablokowane: boolean;
  wysylanie: boolean;
  /** Zdanie pod polem: skrot klawiszowy albo powod blokady. */
  podpowiedz: string;
  /** Blad ostatniej proby wyslania (pokazywany pod polem). */
  blad: BladAkcji | null;
  poleRef: React.Ref<HTMLTextAreaElement>;
}): React.JSX.Element {
  const idBledu = `${ID_POLA}-blad`;
  const bladPodPolem = blad !== null && blad.akcja !== "zakoncz" ? blad : null;
  const pusta = wartosc.trim() === "";
  const opisy = [`${ID_POLA}-hint`, `${ID_POLA}-licznik`, bladPodPolem ? idBledu : null].filter(Boolean).join(" ");

  function wyslij(): void {
    if (zablokowane || pusta) return;
    onWyslij();
  }

  return (
    <div className="trening-pole">
      <Textarea
        id={ID_POLA}
        etykieta="Twoja odpowiedź"
        ref={poleRef}
        value={wartosc}
        rows={4}
        maxLength={MAX_ZNAKOW_WIADOMOSCI_TRENINGU}
        placeholder="Napisz, co odpowiesz rozmówcy"
        hint={podpowiedz}
        readOnly={zablokowane}
        aria-disabled={zablokowane || undefined}
        aria-busy={wysylanie || undefined}
        aria-invalid={bladPodPolem ? true : undefined}
        aria-describedby={opisy}
        onChange={(e) => onZmiana(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            wyslij();
          }
        }}
      />

      {bladPodPolem && (
        <p className="field-error" id={idBledu} role="alert">
          <IconOstrzezenie size={14} />
          {bladPodPolem.zdanie}
        </p>
      )}

      <div className="trening-pole__stopka">
        <LicznikZnakow id={`${ID_POLA}-licznik`} dlugosc={wartosc.length} max={MAX_ZNAKOW_WIADOMOSCI_TRENINGU} />
        <div className="trening-pole__akcje">
          {onOdswiez && (
            <Button wariant="secondary" onClick={onOdswiez}>
              Odśwież rozmowę
            </Button>
          )}
          {bladPodPolem?.mozeSprobowacPonownie && !zablokowane && (
            <Button wariant="secondary" onClick={wyslij}>
              Spróbuj ponownie
            </Button>
          )}
          <Button wariant="primary" zajety={wysylanie} wylaczony={zablokowane || pusta} onClick={wyslij}>
            {!wysylanie && <IkonaWyslij />}
            {wysylanie ? "Wysyłam..." : "Wyślij"}
          </Button>
        </div>
      </div>
    </div>
  );
}
